if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.loadNextPage = this.loadNextPage.bind(this);
    }

    connectedCallback() {
      this.grid = this.closest('.collection')?.querySelector('#product-grid');
      this.sentinel = this;
      this.status = this.querySelector('[data-infinite-scroll-status]');
      this.spinner = this.querySelector('.infinite-scroll__spinner');
      this.fallbackLink = this.querySelector('[data-infinite-scroll-fallback]');
      this.nextUrl = this.dataset.nextUrl;

      if (!this.grid || !this.nextUrl || !('IntersectionObserver' in window)) return;

      this.classList.add('is-enhanced');
      this.observer = new IntersectionObserver(this.loadNextPage, {
        rootMargin: '600px 0px',
        threshold: 0,
      });
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
    }

    async loadNextPage(entries) {
      if (!entries.some((entry) => entry.isIntersecting) || this.loading || !this.nextUrl) return;

      this.loading = true;
      this.observer.unobserve(this.sentinel);
      this.setAttribute('aria-busy', 'true');
      this.spinner?.classList.remove('hidden');
      if (this.status) this.status.textContent = this.dataset.loadingText;

      this.abortController = new AbortController();

      try {
        const requestUrl = new URL(this.nextUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(requestUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Infinite scroll request failed: ${response.status}`);

        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = nextDocument.getElementById('product-grid');
        const nextScroller = nextDocument.querySelector('infinite-scroll');
        const newItems = nextGrid ? Array.from(nextGrid.children) : [];

        if (newItems.length === 0) {
          this.finish();
          return;
        }

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(nextGrid);
        }

        this.grid.append(...newItems);

        this.nextUrl = nextScroller?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;

        this.dispatchEvent(
          new CustomEvent('infinite-scroll:loaded', {
            bubbles: true,
            detail: { itemCount: newItems.length, nextUrl: this.nextUrl },
          }),
        );

        if (this.nextUrl) {
          this.fallbackLink?.setAttribute('href', this.nextUrl);
          if (this.status) this.status.textContent = '';
          this.observer.observe(this.sentinel);
        } else {
          this.finish();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error(error);
        this.classList.add('has-error');
        if (this.status) this.status.textContent = this.dataset.errorText;
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
        this.spinner?.classList.add('hidden');
      }
    }

    finish() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.observer?.disconnect();
      this.fallbackLink?.remove();
      if (this.status) this.status.textContent = this.dataset.endText;
      this.classList.add('is-complete');
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
